import { ImmutableVector3 } from '@modugen/ui-toolkit'

/**
 * Tolerance (in degrees) within which the first segment is treated as aligned with a cardinal
 * global axis (±X). Kept tight since the reference points come from precise user picks.
 */
const AXIS_ALIGNMENT_TOLERANCE_DEG = 1

/**
 * True when `angleDeg` is within {@link AXIS_ALIGNMENT_TOLERANCE_DEG} of `targetDeg`, handling the
 * 0/360° wraparound (e.g. 359.7° is near 0°).
 */
function isNearAngle(angleDeg: number, targetDeg: number): boolean {
  return angularDistanceDeg(angleDeg, targetDeg) <= AXIS_ALIGNMENT_TOLERANCE_DEG
}

/**
 * Z rotation in degrees (0–360) so that after translating the model so the second point
 * is at origin, rotating by this angle aligns the user-defined axes with global X/Y.
 * Uses the first segment (first→second) direction in the XY plane: the stored angle
 * is 180° minus the angle of that segment from global +X, so first segment -X → 0°,
 * first segment +X → 180°. When first segment is -X and second segment (second→third)
 * points in -Y, the stored angle is 90°. When first segment is +X
 * and second segment points in +Y, the stored angle is 270°. When first segment
 * is +Y and second segment points in -X, the stored angle is 180°.
 */
export function getAlignmentAngleDegFromThreePoints(
  first: ImmutableVector3,
  second: ImmutableVector3,
  third: ImmutableVector3,
): number {
  const dx1 = second.x - first.x
  const dy1 = second.y - first.y
  const len1 = Math.hypot(dx1, dy1)
  if (len1 === 0) return 0
  const angle1Deg = (Math.atan2(dy1, dx1) * 180) / Math.PI
  const angleDeg = normalizedAngleDeg(180 - angle1Deg)
  const dx2 = third.x - second.x
  const dy2 = third.y - second.y
  const len2 = Math.hypot(dx2, dy2)
  const angle2Deg = len2 > 0 ? (Math.atan2(dy2, dx2) * 180) / Math.PI : 0

  const firstSegmentIsMinusX = isNearAngle(angleDeg, 0)
  if (firstSegmentIsMinusX) {
    if (len2 > 0 && angle2Deg >= -180 && angle2Deg < -45) return 90
    // Snap the just-below-360° side to a clean 0°; the just-above-0° side already reads as ~0°.
    return angleDeg > 180 ? 0 : angleDeg
  }
  const firstSegmentIsPlusX = isNearAngle(angleDeg, 180)
  if (firstSegmentIsPlusX && len2 > 0 && angle2Deg > 45 && angle2Deg < 135) return 270
  const firstSegmentIsPlusY = angle1Deg > 45 && angle1Deg < 135
  const secondSegmentIsMinusX =
    (angle2Deg > 135 && angle2Deg <= 180) || (angle2Deg >= -180 && angle2Deg < -135)
  if (firstSegmentIsPlusY && len2 > 0 && secondSegmentIsMinusX) return 180
  return angleDeg
}

/**
 * Infer the displayed Z rotation (degrees, 0–360) from the three points.
 * Based on the first leg (first→second) direction in the XY plane, normalized so it matches
 * user expectations (e.g. first leg -x → 0°, +y → 180°, -y → 270°, +x → 270°).
 */
export function inferredAngleDegFromThreePoints(
  first: ImmutableVector3,
  second: ImmutableVector3,
): number {
  const dx = second.x - first.x
  const dy = second.y - first.y
  const firstLegAngleDeg = (Math.atan2(dy, dx) * 180) / Math.PI
  const normalized = ((firstLegAngleDeg % 360) + 360) % 360
  const base = (180 - normalized + 360) % 360
  if (Math.abs(base - 90) < 1) return 180
  if (Math.abs(base - 180) < 1) return 270
  return base
}

export function normalizedAngleDeg(deg: number): number {
  return ((deg % 360) + 360) % 360
}

export function angularDistanceDeg(current: number, target: number): number {
  const n = normalizedAngleDeg(current)
  let diff = Math.abs(n - target)
  if (diff > 180) diff = 360 - diff
  return diff
}

export function nearestAngleWithinOffset(
  current: number,
  targets: number[],
  offset: number,
): number | null {
  const parsed = Number(current)
  if (Number.isNaN(parsed)) return null
  const n = normalizedAngleDeg(parsed)
  let best: { angle: number; dist: number } | null = null
  for (const target of targets) {
    const dist = angularDistanceDeg(n, target)
    if (dist <= offset && (best == null || dist < best.dist)) {
      best = { angle: target, dist }
    }
  }
  return best?.angle ?? null
}
